import Vue from 'vue'
import Router from 'vue-router'
import store from './store'
import { isLoggedIn, removeAuthCookie } from './services/auth'
import { trackPage } from '@/analytics/segment'

import {
  TmsRedirectWrapper,
  UnauthenticatedPage,
  NotFoundPage,
  SystemDownPage
} from './modules/layout'
import {
  Login,
  ResetPassword,
  ForgotPassword,
  Registration,
  RegistrationConfirmation,
  EmailSentConfirmation
} from './modules/authentication'
import TransportRegistrationRoot from './modules/transport-registration'

import marketplaceRoutes from './modules/marketplace/routes'
import myAssignmentsRoutes from './modules/my-assignments/routes'
import myTransportRoutes from './modules/my-transports/routes'

Vue.use(Router)

// Replace Router.push to silence the rejection and make the promise resolve with the error instead:
// https://github.com/vuejs/vue-router/issues/2881#issuecomment-520554378
const originalPush = Router.prototype.push
Router.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject)
  return originalPush.call(this, location).catch(err => err)
} as any

async function handleEnterUnauthenticatedPage(to, from, next) {
  if (isLoggedIn()) {
    next('/')
  } else {
    next()
  }
}

async function handleEnterAuthenticatedPage(to, from, next) {
  if (!isLoggedIn()) {
    if (to.fullPath) {
      next({
        path: '/login',
        query: { redirect: to.path, ...to.query }
      })
    } else {
      parent.postMessage('logout', '*')
      next('/login')
    }
  } else {
    try {
      await store.dispatch('fetchCurrentUser')
    } catch (err) {
      parent.postMessage('logout', '*')
      removeAuthCookie()
      next('/login')
      return
    }

    next()
  }
}

const router = new Router({
  mode: 'history',
  linkActiveClass: 'active',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/auth',
      component: UnauthenticatedPage,
      beforeEnter: handleEnterUnauthenticatedPage,
      children: [
        {
          path: 'registration/password/:uid/:token/:personId',
          component: Registration,
          props: true
        },
        {
          path: 'registration/confirmation',
          component: RegistrationConfirmation
        },
        {
          path: '/login',
          component: Login
        },
        {
          path: 'forgot-password',
          component: ForgotPassword
        },
        {
          path: 'reset-forgotten-password/:uid/:token/:personId',
          component: ResetPassword,
          props: true
        },
        {
          path: 'email-sent',
          component: EmailSentConfirmation
        },
        { path: '/error', redirect: '/' },
        { path: '', redirect: '/not-found' }
      ]
    },
    {
      path: '/',
      component: TmsRedirectWrapper,
      beforeEnter: handleEnterAuthenticatedPage,
      children: [
        { path: '', redirect: 'marketplace' },
        ...marketplaceRoutes,
        ...myAssignmentsRoutes,
        ...myTransportRoutes,
        {
          path: 'transport-registration',
          component: TransportRegistrationRoot
        }
      ]
    },
    { path: '/will-be-back', component: SystemDownPage },
    { path: '*', component: NotFoundPage }
  ]
})

router.beforeEach(async (to, from, next) => {
  document.title = 'Orcas | sennder marketplace'
  next()
})

router.afterEach(() => {
  // track each page visit
  trackPage()
})

export const navigateTo = route => {
  // If the route is the same as the previous one the navigation is going to
  // return a rejected promise, we will just ignore it so it does not appear on
  // Sentry (https://github.com/vuejs/vue-router/issues/2881#issuecomment-520554378)
  router.push(route).catch(() => {})
}

export const openLinkInNewTab = url => {
  window.open(url, '_blank')
}

export default router
